import { lazy, Suspense, useDeferredValue, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/landing/Header";
import AppStoreBadges from "@/components/landing/AppStoreBadges";
import {
  DEFAULT_HORIZON_PAD,
  MAX_HORIZON_AGE,
  milestoneRealBalance,
  projectRetirement,
  resolveTaxRate,
  solveEarliestRetireAge,
  solveRequiredMonthlyContribution,
} from "@/lib/retirement-calc";
import RetirementInputs, { type RetirementFormState } from "./retirement/Inputs";
import ProjectionChart from "./retirement/ProjectionChart";
import Summary from "./retirement/Summary";
import InsightCard from "./retirement/InsightCard";
import ZakatBreakdown from "./retirement/ZakatBreakdown";
import RetirementStickyBanner from "@/components/retire-spoke/RetirementStickyBanner";

const Footer = lazy(() => import("@/components/landing/Footer"));

const CANONICAL = "https://getpiefolio.com/tools/retirement-calculator";
const CURRENCY = "EUR";

const DEFAULT_STATE: RetirementFormState = {
  currentAge: 26,
  targetAge: 65,
  currentSavings: 10_000,
  monthlyContribution: 500,
  monthlyExpenses: 2_500,
  annualReturnPct: 8,
  inflationPct: 2,
  shariahMode: false,
  countryCode: "",
  customTaxRatePct: 0,
  showAfterTax: true,
};

const SCENARIO_AGES = [50, 55, 60, 62, 65, 70];

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

const parseNum = (v: string | null, fallback: number, min: number, max: number) => {
  if (!v) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? clamp(n, min, max) : fallback;
};

const RetirementCalculatorPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialState = useMemo<RetirementFormState>(() => {
    const currentAge = parseNum(searchParams.get("currentAge"), DEFAULT_STATE.currentAge, 12, 75);
    const targetAge = parseNum(
      searchParams.get("targetAge"),
      DEFAULT_STATE.targetAge,
      currentAge + 1,
      80
    );
    return {
      currentAge,
      targetAge,
      currentSavings: parseNum(searchParams.get("savings"), DEFAULT_STATE.currentSavings, 0, 50_000_000),
      monthlyContribution: parseNum(searchParams.get("monthly"), DEFAULT_STATE.monthlyContribution, 0, 2_000_000),
      monthlyExpenses: parseNum(searchParams.get("expenses"), DEFAULT_STATE.monthlyExpenses, 200, 50_000_000),
      annualReturnPct: parseNum(searchParams.get("return"), DEFAULT_STATE.annualReturnPct, 1, 100),
      inflationPct: parseNum(searchParams.get("inflation"), DEFAULT_STATE.inflationPct, 0, 10),
      shariahMode: searchParams.get("shariah") === "1",
      countryCode: searchParams.get("country") ?? DEFAULT_STATE.countryCode,
      customTaxRatePct: parseNum(searchParams.get("customTax"), DEFAULT_STATE.customTaxRatePct, 0, 60),
      showAfterTax: searchParams.get("aftertax") !== "0",
    };
    // initial only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [state, setState] = useState<RetirementFormState>(initialState);

  // Sync URL ?targetAge=… back into state (back/forward nav, deep links).
  useEffect(() => {
    const fromUrl = searchParams.get("targetAge");
    if (!fromUrl) return;
    const n = Number(fromUrl);
    if (!Number.isFinite(n)) return;
    const clamped = clamp(n, state.currentAge + 1, 80);
    if (clamped !== state.targetAge) {
      setState((prev) => ({ ...prev, targetAge: clamped }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setTargetAge = (age: number) => {
    setState((prev) => ({ ...prev, targetAge: clamp(age, prev.currentAge + 1, 80) }));
    const next = new URLSearchParams(searchParams);
    next.set("targetAge", String(age));
    setSearchParams(next, { replace: false });
  };

  const setMonthlyExpenses = (v: number) =>
    setState((prev) => ({ ...prev, monthlyExpenses: clamp(v, 200, 50_000_000) }));

  // Editing the nest-egg target back-solves monthly expenses (target = exp*12*25).
  const setNestEggTarget = (target: number) => {
    const monthly = Math.max(200, Math.round(target / 25 / 12));
    setMonthlyExpenses(monthly);
  };

  // Chart/projection only goes ~12y past retirement — keeps it readable and fast.
  const chartHorizon = useMemo(
    () => Math.min(MAX_HORIZON_AGE, state.targetAge + DEFAULT_HORIZON_PAD),
    [state.targetAge]
  );

  const result = useMemo(
    () => projectRetirement(state, chartHorizon),
    [state, chartHorizon]
  );

  // Defer the expensive solvers so fast slider drags / typing stay smooth.
  const deferredState = useDeferredValue(state);
  const requiredMonthly = useMemo(
    () => solveRequiredMonthlyContribution(deferredState),
    [deferredState]
  );
  const earliestAge = useMemo(
    () => solveEarliestRetireAge(deferredState),
    [deferredState]
  );
  const onTrack = result.onTrack;
  const monthlyExpensesToFit = useMemo(
    () => result.projectedAtRetirementReal / 25 / 12,
    [result]
  );

  // Dynamic milestone tiles: always within the user's remaining runway.
  const milestoneAges = useMemo(() => {
    const candidates = [state.targetAge - 10, state.targetAge - 5, state.targetAge - 2];
    return Array.from(
      new Set(candidates.filter((a) => a > state.currentAge && a < state.targetAge))
    ).sort((a, b) => a - b);
  }, [state.currentAge, state.targetAge]);

  const fmtFull = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: CURRENCY,
        maximumFractionDigits: 0,
      }),
    []
  );
  const fmtCompact = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: CURRENCY,
        maximumFractionDigits: 1,
        notation: "compact",
      }),
    []
  );

  // Dynamic SEO when targetAge is in URL
  const ageInUrl = searchParams.get("targetAge");
  const titledAge = ageInUrl ? state.targetAge : null;
  const title = titledAge
    ? `How much do I need to retire at ${titledAge}? — PieFolio AI`
    : "Retirement Calculator — PieFolio AI";
  const description = titledAge
    ? `Use the 4% rule to see how much you need to retire at ${titledAge}. Inflation-adjusted, with an optional Zakat (~2.58%) toggle for Shariah-aligned planning.`
    : "Plan your retirement with the 4% rule: live projections, a monthly-contribution solver, inflation-adjusted figures and optional Zakat.";

  const appJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "PieFolio Retirement Calculator",
    url: CANONICAL,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web, iOS, Android",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://getpiefolio.com/" },
      { "@type": "ListItem", position: 2, name: "Tools", item: "https://getpiefolio.com/tools/dividend-calculator" },
      { "@type": "ListItem", position: 3, name: title, item: CANONICAL },
    ],
  };

  const faqs = useMemo(
    () => [
      {
        q: "What do I need to do now to retire at 55?",
        a: "Three levers: (1) raise your monthly contribution to whatever amount the solver above shows, (2) push retirement back a few years so compounding does more of the work, or (3) lower retirement spending so the 25× target shrinks. The calculator turns your own numbers into a single $/mo figure to aim for.",
      },
      {
        q: "How much should I have by age 50 to retire?",
        a: "It depends on your retirement age and spending. As a glide path: to retire at 65 on $50,000/yr ($1.25M target), you'd want roughly $600K–$750K in today's dollars by 50 if you keep saving and earn ~7% real. The milestone tiles above show the number tailored to your inputs.",
      },
      {
        q: "How much do I need to retire at 60 or 65?",
        a: "The target is the same — 25× annual expenses — regardless of age. What changes is how aggressively you need to save. Retiring later means more years of compounding, so you can get there with smaller monthly contributions.",
      },
      {
        q: "What is the 4% rule?",
        a: "It's a retirement guideline: you can withdraw 4% of your portfolio in year one of retirement, then adjust for inflation each year, with a high probability of the money lasting 30+ years. The inverse is the 25× rule: save 25 times your annual expenses.",
      },
      {
        q: "How much should I save each month for retirement?",
        a: "It depends on your current age, savings, target age, and expected return. As a rough rule of thumb, saving 15% of gross income from age 25 puts most people on track. The 'Increase contribution' suggestion above gives you a personalized number based on your inputs.",
      },
      {
        q: "How is Zakat handled?",
        a: "When the Zakat toggle is on, the calculator deducts ~2.58% per year from the portfolio (the solar-year equivalent of the traditional 2.5% lunar-year Zakat). It's a planning aid and doesn't replace guidance from a qualified scholar on what counts as zakatable wealth.",
      },
      {
        q: "Why adjust for inflation?",
        a: "A dollar in 2050 buys less than a dollar today. Showing the projection in today's money lets you compare your retirement balance directly to today's expenses — which is the basis the 4% rule was built on.",
      },
    ],
    []
  );

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(appJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <Header />

      <main className="pt-24">
        <header className="container mx-auto px-6 lg:px-12 max-w-4xl text-center py-8 sm:py-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-3">
            Retirement <span className="text-gradient-sage">Calculator</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            See exactly how much you need, how much to save each month, and when you can
            stop working — using the 4% rule, adjusted for inflation.
          </p>
          <div className="flex justify-center">
            <AppStoreBadges
              variant="dark"
              size="md"
              align="center"
              location="retirement_calculator_hero"
              utmCampaign="retirement_hub"
            />
          </div>
        </header>

        <section className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-6xl pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5 lg:gap-8">
            {/* Inputs — last on mobile, first (left rail) on desktop */}
            <div className="order-2 lg:order-1">
              <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm">
                <RetirementInputs
                  state={state}
                  onChange={setState}
                  onReset={() => setState(DEFAULT_STATE)}
                />
              </div>
            </div>

            {/* Results */}
            <div className="order-1 lg:order-2 space-y-5 min-w-0">
              <InsightCard
                result={result}
                targetAge={state.targetAge}
                currentAge={state.currentAge}
                monthlyExpenses={state.monthlyExpenses}
                monthlyContribution={state.monthlyContribution}
                requiredMonthly={requiredMonthly}
                earliestAge={earliestAge}
                fmtCompact={fmtCompact}
                fmtFull={fmtFull}
                onTargetAgeChange={setTargetAge}
                onMonthlyExpensesChange={setMonthlyExpenses}
                onNestEggTargetChange={setNestEggTarget}
              />

              <Summary
                result={result}
                fmtCompact={fmtCompact}
                fmtFull={fmtFull}
                inflationPct={state.inflationPct}
                shariahMode={state.shariahMode}
                showAfterTax={state.showAfterTax}
                taxRatePct={resolveTaxRate(state) * 100}
              />


              {state.shariahMode && (
                <ZakatBreakdown
                  result={result}
                  fmtFull={fmtFull}
                  targetAge={state.targetAge}
                />
              )}


              <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm">
                <div className="flex items-baseline justify-between mb-3">
                  <h2 className="text-lg font-bold">Asset growth vs target</h2>
                  <span className="text-xs text-muted-foreground">Today's $</span>
                </div>
                <ProjectionChart
                  rows={result.rows}
                  currency={CURRENCY}
                  locale="en-US"
                  shariahMode={state.shariahMode}
                  targetAge={state.targetAge}
                />
              </div>

              {/* Scenario buttons — instantly update the calculator + URL */}
              <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm">
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
                  Explore by retirement age
                </h2>
                <div className="flex flex-wrap gap-2">
                  {SCENARIO_AGES.map((age) => {
                    const active = state.targetAge === age;
                    return (
                      <button
                        key={age}
                        type="button"
                        onClick={() => setTargetAge(age)}
                        aria-pressed={active}
                        className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border hover:border-primary/50 hover:text-primary"
                        }`}
                      >
                        Retire at {age}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                    In-depth guides
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <a href="/blog/how-much-to-retire-at-50" className="text-primary hover:underline font-medium">
                      How much to retire at 50 →
                    </a>
                    <a href="/blog/how-much-to-retire-at-55" className="text-primary hover:underline font-medium">
                      How much to retire at 55 →
                    </a>
                    <a href="/blog/how-much-to-retire-at-60" className="text-primary hover:underline font-medium">
                      How much to retire at 60 →
                    </a>
                  </div>
                </div>
              </div>

              {/* SEO answer block A — dynamic action plan */}
              <section
                id={`retire-at-${state.targetAge}`}
                className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm"
              >
                <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight mb-2">
                  What do I need to do now to retire at {state.targetAge}?
                </h2>
                <p className="text-sm sm:text-base text-foreground/80 leading-relaxed mb-4">
                  {onTrack ? (
                    <>
                      You're on pace. At{" "}
                      <strong className="tabular-nums">
                        {fmtFull.format(state.monthlyContribution)}/mo
                      </strong>{" "}
                      you'll clear your{" "}
                      <strong>{fmtCompact.format(result.nestEggTargetToday)}</strong>{" "}
                      target by age {state.targetAge}. Keep going — and consider any of the
                      levers below to retire earlier or with more cushion.
                    </>
                  ) : (
                    <>
                      To retire at {state.targetAge} on{" "}
                      <strong className="tabular-nums">
                        {fmtFull.format(state.monthlyExpenses)}/mo
                      </strong>
                      , you need{" "}
                      <strong>{fmtCompact.format(result.nestEggTargetToday)}</strong>{" "}
                      (25× annual expenses). Your current pace gets you to{" "}
                      <strong className="tabular-nums">
                        {fmtCompact.format(result.projectedAtRetirementReal)}
                      </strong>{" "}
                      — pick any one of the levers below to close the gap.
                    </>
                  )}
                </p>
                <ol className="space-y-2.5 text-sm sm:text-base">
                  {Number.isFinite(requiredMonthly) && requiredMonthly > 0 && (
                    <li className="flex gap-3">
                      <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center">
                        1
                      </span>
                      <span>
                        Save{" "}
                        <strong className="tabular-nums">
                          {fmtFull.format(requiredMonthly)}/mo
                        </strong>{" "}
                        instead of{" "}
                        <span className="tabular-nums">
                          {fmtFull.format(state.monthlyContribution)}/mo
                        </span>{" "}
                        — that lands you exactly at your target by {state.targetAge}.
                      </span>
                    </li>
                  )}
                  {earliestAge && earliestAge !== state.targetAge && (
                    <li className="flex gap-3">
                      <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center">
                        2
                      </span>
                      <span>
                        Or keep your current pace and retire at{" "}
                        <strong>age {earliestAge}</strong> instead.
                      </span>
                    </li>
                  )}
                  {monthlyExpensesToFit > 0 && monthlyExpensesToFit < state.monthlyExpenses && (
                    <li className="flex gap-3">
                      <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center">
                        3
                      </span>
                      <span>
                        Or trim retirement spending to{" "}
                        <strong className="tabular-nums">
                          {fmtFull.format(Math.floor(monthlyExpensesToFit / 50) * 50)}/mo
                        </strong>{" "}
                        — your current savings rate covers that lifestyle at {state.targetAge}.
                      </span>
                    </li>
                  )}
                </ol>
              </section>

              {/* SEO answer block B — milestone glide path */}
              {milestoneAges.length > 0 && (
                <section className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm">
                  <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight mb-2">
                    How much should I have at each age to retire at {state.targetAge}?
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Today's-$ balance you'd need at each milestone, assuming you keep
                    contributing {fmtFull.format(state.monthlyContribution)}/mo at a{" "}
                    {state.annualReturnPct}% return until {state.targetAge}.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {milestoneAges.map((age) => {
                      const v = milestoneRealBalance(state, age);
                      if (v == null) return null;
                      return (
                        <div
                          key={age}
                          className="rounded-xl border border-border/70 bg-background/50 p-3"
                        >
                          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                            By age {age}
                          </div>
                          <div className="text-lg sm:text-xl font-extrabold tabular-nums mt-0.5">
                            {fmtCompact.format(v)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          </div>
        </section>

        <section className="container mx-auto px-6 lg:px-12 max-w-3xl py-12">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-6">
            How it works
          </h2>
          <div className="space-y-4 text-foreground/80 leading-relaxed text-sm sm:text-base">
            <p>
              The 4% rule (also called the 25× rule) is a widely-cited retirement guideline:
              if you save 25 times your annual expenses, you can withdraw 4% each year with
              a high probability of the money lasting 30+ years.
            </p>
            <p>
              We project your balance year by year using your contributions and expected
              return, then deflate it by inflation so the result is comparable to today's
              expenses — the same basis the 4% rule was designed on. The "increase
              contribution" number above is solved by finding the monthly amount that makes
              the projected real balance exactly meet your target.
            </p>
            <p>
              The optional Zakat toggle deducts ~2.58% per year (the solar-year equivalent
              of 2.5% per lunar year), treating it like a tax line. It's a planning aid only
              and doesn't replace guidance from a qualified scholar on what counts as
              zakatable wealth.
            </p>
          </div>
        </section>

        <section className="container mx-auto px-6 lg:px-12 max-w-3xl pb-16">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-6">FAQ</h2>
          <div className="space-y-3">
            {faqs.map((f) => (
              <details
                key={f.q}
                className="group rounded-xl border border-border bg-card p-4"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="font-semibold">{f.q}</span>
                  <span className="text-primary group-open:rotate-45 transition-transform text-xl leading-none shrink-0">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-foreground/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      </main>
      <RetirementStickyBanner location="retirement_calculator_sticky" />
    </div>
  );
};

export default RetirementCalculatorPage;
